import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";
import { useAuthStore } from "@/stores/authStore";
import { getRequestGeneric } from "@/services/backendApiGeneric";
import {
  MAX_RECENT,
  addVisit,
  parseEntries,
  reconcileEntries,
  removeEntries,
  type RecentCaseEntry,
} from "@/stores/recentCasesList";

/**
 * 26.07.26 VIII: Merkt sich die zuletzt geöffneten Fälle (max. 10), damit
 * sie über den Pfeil am "Fälle"-Button in der Hauptnavigation schnell
 * wieder aufgerufen werden können (Direktabsprung ins Cockpit).
 *
 * Persistenz über localStorage, damit die Liste einen Reload überlebt.
 *
 * 30.08.26 Datenschutz-Fix: Die Liste ist pro Benutzer UND Organisation
 * gescoped. Vorher lag sie unter einem globalen Key, wodurch z. B. bei
 * SuperAdmin-Impersonation oder Org-Wechsel im selben Browser Fallnamen
 * einer fremden Kanzlei sichtbar wurden. Ohne authentifizierten Kontext
 * wird weder gelesen noch geschrieben.
 *
 * 09.09.26 (Mail RA Polz): Die Liste war bis hierher eine Einbahnstraße —
 * geschrieben wurde immer, entfernt nie. Nach dem endgültigen Löschen einer
 * Akte blieben Fall-ID und Mandantenname unbegrenzt im localStorage stehen und
 * wurden weiter im Menü angezeigt, auch wenn der Account gar keine Fälle mehr
 * hatte. Deshalb jetzt zwei Wege hinaus:
 *   - remove()   räumt den gerade gelöschten Fall sofort weg.
 *   - validate() gleicht die ganze Liste mit dem Server ab und fängt damit
 *     auch Löschungen durch Kolleginnen, andere Geräte und Altbestände.
 * Die reine Listenlogik steht in recentCasesList.ts (dort auch die Tests).
 */

const STORAGE_KEY_PREFIX = "kars.recentCases";
/** Alter, globaler (ungescopeter) Key — wird beim ersten Laden entsorgt. */
const LEGACY_STORAGE_KEY = "kars.recentCases";

export type { RecentCaseEntry };
export { MAX_RECENT };

function loadEntries(storageKey: string): RecentCaseEntry[] {
  try {
    return parseEntries(localStorage.getItem(storageKey));
  } catch {
    return [];
  }
}

function persist(storageKey: string, entries: RecentCaseEntry[]) {
  try {
    localStorage.setItem(storageKey, JSON.stringify(entries));
  } catch {
    // localStorage voll/gesperrt — Liste lebt dann nur im SPA-Speicher.
  }
}

/**
 * Einmalige Bereinigung: Einträge unter dem alten globalen Key lassen sich
 * keinem Mandanten zuordnen und werden daher gelöscht statt migriert.
 */
function removeLegacyGlobalList() {
  try {
    localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch {
    // no-op
  }
}

export const useRecentCasesStore = defineStore("recentCases", () => {
  removeLegacyGlobalList();

  const auth = useAuthStore();

  /**
   * Storage-Key des aktuellen Auth-Kontexts, null solange niemand
   * angemeldet ist. Benutzer- UND Organisations-ID, damit weder zwei
   * Benutzer im selben Browser noch derselbe Benutzer in zwei
   * Organisationen (Org-Switch/Impersonation) die Listen des jeweils
   * anderen Kontexts sehen.
   */
  const scopeKey = computed<string | null>(() => {
    const idUser = Number(auth.user?.idUser) || 0;
    const idOrganization = Number(auth.user?.idOrganization) || 0;
    if (idUser <= 0 || idOrganization <= 0) return null;
    return `${STORAGE_KEY_PREFIX}.u${idUser}.o${idOrganization}`;
  });

  const entries = ref<RecentCaseEntry[]>([]);
  /** Verhindert parallele Abgleiche, wenn das Menü mehrfach geöffnet wird. */
  const validating = ref(false);

  /** Liste im aktuellen Scope setzen und wegschreiben. */
  function commit(key: string, next: RecentCaseEntry[]) {
    entries.value = next;
    persist(key, next);
  }

  watch(
    scopeKey,
    (key) => {
      entries.value = key ? loadEntries(key) : [];
      // Beim Anmelden bzw. Kontextwechsel einmal gegen den Server prüfen —
      // sonst begrüßt uns die Historie mit längst gelöschten Akten.
      if (key) validate();
    },
    { immediate: true },
  );

  /** Fall-Aufruf registrieren (dedupe, neueste zuerst, max. 10). */
  function recordVisit(id: number | string | null, label?: string | null) {
    const key = scopeKey.value;
    if (!key) return;
    commit(key, addVisit(entries.value, id, label));
  }

  /**
   * Fall aus der Liste entfernen — nach dem endgültigen Löschen aufrufen,
   * damit Bezeichnung und ID sofort verschwinden und nicht erst beim
   * nächsten Abgleich.
   */
  function remove(ids: number | string | ReadonlyArray<number | string>) {
    const key = scopeKey.value;
    if (!key) return;
    commit(key, removeEntries(entries.value, ids));
  }

  /**
   * Liste mit dem Server abgleichen: gelöschte oder nicht mehr zugängliche
   * Akten fliegen raus, umbenannte bekommen ihre aktuelle Bezeichnung.
   *
   * Fehler bleiben bewusst stumm und lassen die Liste unangetastet: ein
   * Verbindungsproblem darf die Historie weder leeren noch den Nutzer mit
   * einer Meldung behelligen, die er nicht ausgelöst hat.
   */
  async function validate() {
    const key = scopeKey.value;
    if (!key || validating.value) return;
    if (entries.value.length === 0) return;

    validating.value = true;
    try {
      const askedIds = entries.value.map((e) => e.id);
      const knownCases = await getRequestGeneric(`/protected/cases/recent?ids=${askedIds.join(",")}`);
      // Zwischenzeitlicher Kontextwechsel (Org-Switch, Logout): Antwort
      // gehört dann zu einem anderen Scope und wird verworfen.
      if (scopeKey.value !== key) return;
      // askedIds mitgeben: Fälle, die der Nutzer während der Anfrage geöffnet
      // hat, stehen nicht in der Antwort und dürfen nicht wegsortiert werden.
      commit(key, reconcileEntries(entries.value, knownCases, askedIds));
    } catch {
      // Liste bleibt, wie sie ist.
    } finally {
      validating.value = false;
    }
  }

  return { entries, recordVisit, remove, validate };
});
